import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
    getNotifications,
    getNotificationStats,
    deleteNotification,
    sendNotification,
} from "../services/adminApi";

import NotificationHeader from "../components/notifications/NotificationHeader";
import NotificationStats from "../components/notifications/NotificationStats";
import NotificationFilterBar from "../components/notifications/NotificationFilterBar";
import NotificationTable from "../components/notifications/NotificationTable";

import "../styles/Notifications.css";

const Notifications = () => {
    const navigate = useNavigate();

    const [notifications, setNotifications] = useState([]);
    const [stats, setStats] = useState({});

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [search, setSearch] = useState("");
    const [audience, setAudience] = useState("all");
    const [status, setStatus] = useState("all");

    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            setError("");

            const [notificationRes, statsRes] = await Promise.all([
                getNotifications(),
                getNotificationStats(),
            ]);

            setNotifications(notificationRes.data.notifications || []);
            setStats(statsRes.data.stats || {});
        } catch (err) {
            console.error(err);
            setError(
                err?.response?.data?.message ||
                    "Failed to load notifications."
            );
        } finally {
            setLoading(false);
        }
    };

    const filteredNotifications = useMemo(() => {
        let data = [...notifications];

        if (search.trim()) {
            const value = search.toLowerCase();

            data = data.filter(
                (item) =>
                    item.title?.toLowerCase().includes(value) ||
                    item.message?.toLowerCase().includes(value)
            );
        }

        if (audience !== "all") {
            data = data.filter(
                (item) => item.audience === audience
            );
        }

        if (status !== "all") {
            data = data.filter(
                (item) => item.status === status
            );
        }

        return data;
    }, [notifications, search, audience, status]);

    const handleDelete = async (id) => {
        const confirmDelete = window.confirm(
            "Are you sure you want to delete this notification?"
        );

        if (!confirmDelete) return;

        try {
            setActionLoading(true);

            await deleteNotification(id);

            await loadData();
        } catch (err) {
            console.error(err);
            alert(
                err?.response?.data?.message ||
                    "Failed to delete notification."
            );
        } finally {
            setActionLoading(false);
        }
    };

    const handleSend = async (id) => {
        const confirmSend = window.confirm(
            "Send this notification now?"
        );

        if (!confirmSend) return;

        try {
            setActionLoading(true);

            await sendNotification(id);

            await loadData();
        } catch (err) {
            console.error(err);
            alert(
                err?.response?.data?.message ||
                    "Failed to send notification."
            );
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="admin-loading">
                Loading Notifications...
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-error">
                {error}
            </div>
        );
    }

    return (
        <div className="notifications-page">

            <NotificationHeader
                onCreate={() =>
                    navigate("/admin/notifications/create")
                }
            />

            <NotificationStats stats={stats} />

            <NotificationFilterBar
                search={search}
                setSearch={setSearch}
                audience={audience}
                setAudience={setAudience}
                status={status}
                setStatus={setStatus}
            />

            <NotificationTable
                notifications={filteredNotifications}
                loading={actionLoading}
                onView={(id) =>
                    navigate(`/admin/notifications/${id}`)
                }
                onEdit={(id) =>
                    navigate(`/admin/notifications/edit/${id}`)
                }
                onDelete={handleDelete}
                onSend={handleSend}
            />

            {!filteredNotifications.length && (
                <div className="empty-notification-state">
                    <h3>No Notifications Found</h3>
                    <p>
                        Create your first notification or
                        change the filters.
                    </p>
                </div>
            )}

        </div>
    );
};

export default Notifications;